'use client'

import React from "react";
import Headline from "@/components/ui/Headline/Headline";
import LPForm from "@/components/LPForm/LPForm";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { useIsLg, useIsMidxl2, useIsTb } from '@/contexts/BreakpointContext';

interface LPCtaFormSectionProps {
  language?: LanguageType;
  headlineText: string;
  subheadingText: string;
  paragraphText: string;
  benefits: string[];
  formHeadlineText: string;
  formSubheadingText: string;
  onFormSubmit?: (formData: any) => void;
  id?: string;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const LPCtaFormSection: React.FC<LPCtaFormSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  paragraphText,
  benefits,
  formHeadlineText,
  formSubheadingText,
  onFormSubmit,
  id = "cta-form-section"
}) => {
  const isMidxl2 = useIsMidxl2();
  const isLg = useIsLg();
  const isTb = useIsTb();

  return (
    <section
      id={id}
      style={{
        direction: LANGUAGE_TO_DIRECTION[language],
        marginTop: '-4.5rem',
        paddingTop: isTb ? 'calc(13.5rem + 80px)' : 'calc(7rem + 80px)'
      }}

      // lg:pt-[13.5rem]
        // bg-[#6d5941]
      className="
        w-full flex items-center justify-center
        padding-main-x-mobile
        padding-main-x-desktop
        pb-[1rem]
        lg:pb-[7rem]
        relative
        rounded-[24px] lg:rounded-[56px]
      "
    >
      {/* Background Images */}
      <div className="bg-partial-circle-wrapper rounded-b-[24px] lg:rounded-b-[56px]">
        <div className="bg-partial-circle"></div>
      </div>

      <div className="bg-lower-part rounded-b-[24px] lg:rounded-b-[56px]">


      </div>

      <div
        // ref={shadowRef}
        className="absolute w-full aspect-3/4 tb:aspect-3/2 z-10 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          top: '0px',
          transform: '',
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      />
      <div className="w-full z-10 container-x-small flex items-center justify-center">
        <div className="w-full flex flex-col gap-[2rem] lg:gap-[3.5rem] items-center justify-center">

          {/* Headline and Benefits Section */}
          <div className="w-full flex flex-col gap-[2.5rem] items-center justify-center">
            <div className="flex items-center justify-center w-full">
              <Headline
                subheading={subheadingText}
                heading={headlineText}
                type="short"
                alignment="center"
                paragraph={paragraphText}
                language={language}
                textColor="text-text-reversed"
                reversedParagraphColor={true}
                shouldAnimate={false}
              />
            </div>

            {/* Benefits - Desktop Only */}
            {isMidxl2 && (
              <div className="w-full flex items-center justify-center">
                <div
                  style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                  className="w-full max-w-[43.3125rem] flex flex-row items-center justify-center gap-[1.5rem]"
                >
                  <p className="text-text-reversed text-14--alexandria text-center py-[0.5rem] min-w-[12.2rem] w-fit">
                    {benefits[0]}
                  </p>

                  <div className="w-[1.5px] h-[3.5rem] bg-stroke-sub-300"></div>

                  <p className="text-text-reversed text-14--alexandria text-center py-[0.5rem] min-w-[12.2rem] w-fit">
                    {benefits[1]}
                  </p>

                  <div className="w-[1.5px] h-[3.5rem] bg-stroke-sub-300"></div>

                  <p className="text-text-reversed text-14--alexandria text-center py-[0.5rem] min-w-[12.2rem] w-fit">
                    {benefits[2]}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Form Section */}
          <div className="w-full flex items-center justify-center">
            <LPForm
              language={language}
              headlineText={formHeadlineText}
              subheadingText={formSubheadingText}
              benefits={benefits}
              onSubmit={onFormSubmit}
              borderColor={isLg ? "border-primary-base" : "border-stroke-soft-200"}
              shadowClass={isMidxl2 ? "shadow-sunshine-medium-glow" : "shadow-sunshine-primary-glow"}
              innerHorizontalPadding="px-0 tb:px-[5rem]"
              innerVerticalPadding="p-[1.5rem] tb:pt-[5rem] tb:pb-[3.5rem]"
            />
          </div>

        </div>
      </div>
    </section>
  );
};

export default LPCtaFormSection;
